import type { jsPDF } from 'jspdf';
import { InfrastructureByYearPerMonth, ReportFilters, MONTH_FULL_NAMES } from '../../../types/types';
import { computeInfrastructureMetrics } from '../../../utils/summary_report/metrics/infrastructureMetrics';
import { drawSectionHeader, drawDataList, ensureSpace } from '../../../utils/summary_report/pdfPrimitives';
import { colors, layout, fonts } from '../../../theme/themes';

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

/** "September 2026, by category" when a month is selected, or a YTD-total
 * label when mode is 'year' with nothing selected — same fallback shape
 * as the areas-at-risk subtitle, but framed as a sum (this IS a flow). */
function buildSubtitle(filters: ReportFilters): string {
  const includesMonth = filters.mode === 'month' || filters.mode === 'both';
  if (includesMonth && filters.month) {
    return `${MONTH_FULL_NAMES[filters.month]} ${filters.year}, by category`;
  }
  return `Jan\u2013Dec ${filters.year} total, by category`;
}

/**
 * Draws the infrastructure-destroyed section starting at `startY` and
 * returns the y-coordinate the next section should start at.
 *
 * Unlike areas-at-risk, this data IS a flow (new destruction recorded each
 * month), so it follows the same YTD-summing convention as eviction
 * numbers — a month selection shows that month directly; year-only mode
 * shows the full-year sum, not a single-month fallback.
 *
 * Laid out as a compact 4-column grid (2 rows for 7 categories) rather
 * than a tall single-column list or a bar chart — deliberately small so
 * this section doesn't push areas-at-risk onto its own page.
 */
export function drawInfrastructureSection(
  doc: jsPDF,
  data: InfrastructureByYearPerMonth,
  filters: ReportFilters,
  startY: number,
  contentWidth: number,
): number {
  const x = layout.pageMarginX;
  const metrics = computeInfrastructureMetrics(data, filters);
  const includesMonth = filters.mode === 'month' || filters.mode === 'both';

  const items = metrics.categories.map((c) => {
    const value = includesMonth && c.month !== null ? c.month : c.ytd;
    return { label: c.label, value: formatNumber(value) };
  });

  const columns = 4;
  const boxPadding = 12;
  const colGap = 10;
  const rowGap = 14;
  const labelLineHeight = 9;
  const labelToValueGap = 4;
  const valueLineHeight = 15;
  const rowContentHeight = labelLineHeight + labelToValueGap + valueLineHeight;

  const rows = Math.ceil(items.length / columns);
  const colWidth = (contentWidth - boxPadding * 2 - colGap * (columns - 1)) / columns;
  const gridHeight = rows * rowContentHeight + (rows - 1) * rowGap;
  const boxHeight = gridHeight + boxPadding * 2;

  const totalSectionHeight = 20 + boxHeight + 20;
  const adjustedStartY = ensureSpace(doc, startY, totalSectionHeight);

  const y = drawSectionHeader(
    doc,
    x,
    adjustedStartY,
    contentWidth,
    '02',
    'Infrastructure destroyed',
    buildSubtitle(filters),
  );

  doc.setDrawColor(...colors.stone200);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, contentWidth, boxHeight, layout.cardRadius, layout.cardRadius, 'D');

  items.forEach((item, i) => {
    const col = i % columns;
    const row = Math.floor(i / columns);
    const cellX = x + boxPadding + col * (colWidth + colGap);
    const cellY = y + boxPadding + row * (rowContentHeight + rowGap);

    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...colors.stone500);
    doc.text(item.label, cellX, cellY + labelLineHeight);

    doc.setFont(fonts.mono, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...colors.stone900);
    doc.text(item.value, cellX, cellY + labelLineHeight + labelToValueGap + valueLineHeight - 3);
  });

  return y + boxHeight + 20; // gap before next section — matches eviction numbers' spacing, so every section's title has the same top padding
}